import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as XLSX from "xlsx";
import {
  SHOP_ZONE_CONFLICT,
  ShopZoneRow,
  getShopZones,
  patchShopZone,
  readShopZoneError,
} from "../../api/shopZone";
import { PageLayout } from "../../components/PageLayout";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";
import {
  ImportPlan,
  RADIUS_MAX,
  RADIUS_MIN,
  Zone,
  fix8,
  isChanged,
  isFar,
  movedM,
  pickCols,
  planImport,
  stamp,
  toZone,
} from "./shopZoneData";

/**
 * 임대사업소 안전구역 지도 (인플랩 관리자 전용)
 *
 * 예전 단독 페이지(임대사업소_지도_카카오.html)를 옮긴 것이다. 달라진 점은 하나다.
 * 그 페이지는 수정분을 브라우저에 모았다가 엑셀·SQL 로 내보냈지만,
 * 이 화면은 마커를 놓거나 반경을 적용하는 순간 tb_shop_zone 에 바로 저장한다.
 * 수집API서버가 이벤트마다 이 테이블을 다시 읽으므로 저장 즉시 알람 억제 범위가 바뀐다.
 *
 * 동시 수정: 저장할 때 화면이 받은 수정일시를 같이 보낸다. 그 사이 다른 사람이 고쳤으면
 * 서버가 거절하고 지금 값을 돌려주므로 그 값으로 바꿔 보여 준다.
 */

const { md } = theme.screen;

const ZOOM_PICK = 2; // 카카오 레벨. 낮을수록 확대
const MIN_BODY_HEIGHT = 480;
/** 본문 아래 여백 (다른 화면 본문 상자와 같은 20px) */
const BODY_MARGIN_BOTTOM = 20;
const RADIUS_PRESETS = [30, 50, 80, 100, 150, 200];

// 항공사진 위에서 눈에 띄어야 한다. 수정 표시는 자연에 거의 없는 형광 자홍으로 둔다
const EDIT_HUE = "#ff1f8f";
const FAR_HUE = "#c0392b";
const NEAR_HUE = "#d9741f";

const MAP_TYPES = [
  { key: "ROADMAP", label: "일반" },
  { key: "SKYVIEW", label: "위성" },
  { key: "HYBRID", label: "하이브리드" },
] as const;
type MapTypeKey = (typeof MAP_TYPES)[number]["key"];

const esc = (s: unknown) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string)
  );

type Kind = "edit" | "far" | "near";

interface Toast {
  html: string;
  bad?: boolean;
}

const getKakao = (): any => (window as any).kakao;

function pinImage(kakao: any, kind: Kind) {
  if (kind === "edit") {
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30">' +
      `<circle cx="15" cy="15" r="13" fill="${EDIT_HUE}" fill-opacity=".28"/>` +
      '<circle cx="15" cy="15" r="9.5" fill="none" stroke="#2b0016" stroke-width="4"/>' +
      `<circle cx="15" cy="15" r="9.5" fill="${EDIT_HUE}" stroke="#fff" stroke-width="2.6"/>` +
      '<circle cx="15" cy="15" r="3" fill="#fff"/></svg>';
    return new kakao.maps.MarkerImage(
      "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      new kakao.maps.Size(30, 30),
      { offset: new kakao.maps.Point(15, 15) }
    );
  }
  const fill = kind === "far" ? FAR_HUE : NEAR_HUE;
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">' +
    '<circle cx="11" cy="11" r="7" fill="none" stroke="#1a1207" stroke-width="3.4"/>' +
    `<circle cx="11" cy="11" r="7" fill="${fill}" stroke="#fff" stroke-width="2.2"/></svg>`;
  return new kakao.maps.MarkerImage(
    "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    new kakao.maps.Size(22, 22),
    { offset: new kakao.maps.Point(11, 11) }
  );
}

const ShopZone = () => {
  const user = userStorage.get();
  const isAllAdmin = user?.area === "00000" && user?.grade === 2;
  const { width } = useWindowSize();
  const isMobile = width <= md;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["shopZones"],
    queryFn: getShopZones,
    enabled: isAllAdmin,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // ── 상태 ──────────────────────────────────────────────────
  const [zones, setZones] = useState<Zone[]>([]);
  // 이번 접속에서 처음 받은 값. '수정함' 표시와 되돌리기의 기준이다
  const origRef = useRef<Map<number, Zone>>(new Map());
  const zonesRef = useRef<Zone[]>([]);
  zonesRef.current = zones;

  const [q, setQ] = useState("");
  const [farOnly, setFarOnly] = useState(false);
  const [mapType, setMapType] = useState<MapTypeKey>("ROADMAP");
  const [district, setDistrict] = useState(false);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [rTargetId, setRTargetId] = useState<number | null>(null);
  const [rInput, setRInput] = useState("50");
  const [rMsg, setRMsg] = useState("");
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [bulk, setBulk] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [mapFailed, setMapFailed] = useState(false);

  useEffect(() => {
    if (!data) return;
    const next = (data as ShopZoneRow[]).map(toZone);
    for (const z of next) {
      if (!origRef.current.has(z.id)) origRef.current.set(z.id, z);
    }
    setZones(next);
  }, [data]);

  const orig = (id: number) => origRef.current.get(id);
  // origRef 는 zones 보다 먼저 채워지므로 zones 만 보고 다시 계산하면 된다
  const changedIds = useMemo(
    () => zones.filter((z) => isChanged(z, origRef.current.get(z.id))).map((z) => z.id),
    [zones]
  );
  const kindOf = useCallback(
    (z: Zone): Kind =>
      isChanged(z, origRef.current.get(z.id)) ? "edit" : isFar(z) ? "far" : "near",
    []
  );

  // ── 화면 높이 ────────────────────────────────────────────────
  // 상단바·브레드크럼 높이가 화면 폭에 따라 달라서, 본문이 시작하는 위치를 재서 남은 높이를 준다.
  // 다른 화면처럼 아래에 20px 여백을 두고, 꼬리말은 그 아래(스크롤)에 둔다
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => {
      const top = el.getBoundingClientRect().top;
      setBodyHeight(Math.max(MIN_BODY_HEIGHT, window.innerHeight - top - BODY_MARGIN_BOTTOM));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [width, isAllAdmin]);

  // ── 지도 ──────────────────────────────────────────────────
  const mapElRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const infowRef = useRef<any>(null);
  const iconRef = useRef<Record<Kind, any> | null>(null);
  const marksRef = useRef<Map<number, { marker: any; circle: any }>>(new Map());
  const [mapReady, setMapReady] = useState(false);

  const popupHtml = useCallback((z: Zone) => {
    const o = origRef.current.get(z.id);
    const mv = movedM(z, o);
    const nv = `https://map.naver.com/p?c=${z.lot},${z.lat},18,0,0,0,dh`;
    const kk = `https://map.kakao.com/link/map/${encodeURIComponent(z.nm)},${z.lat},${z.lot}`;
    const gg = `https://www.google.com/maps/@${z.lat},${z.lot},19z/data=!3m1!1e3`;
    const d = z.diff === null ? "" : `<br>운행기록 추출좌표와 ${z.diff.toLocaleString()}m`;
    const e =
      o && isChanged(z, o)
        ? `<br><strong>이번 접속에서 수정</strong> — ${mv.toLocaleString()}m 이동, 반경 ${o.r}m → ${z.r}m`
        : "";
    return `<div style="padding:10px 12px;font-size:13px;color:#22261f;max-width:300px;line-height:1.45">
      <div style="font-weight:600;margin-bottom:4px">${esc(z.nm)}</div>
      <div style="color:#6c7266;font-size:12px">${esc(z.addr)}${d}${e}</div>
      <div style="font-family:Consolas,monospace;font-size:11px;margin-top:6px">${z.lat.toFixed(8)}, ${z.lot.toFixed(8)} · 반경 ${z.r}m · ogdp ${esc(z.ogdp)}</div>
      <div style="font-size:11px;color:#6c7266;margin-top:2px">수정일시 ${esc(z.mdfcn)}</div>
      <div style="margin-top:7px;display:flex;gap:8px;font-size:11.5px">
        <a href="${nv}" target="_blank" rel="noopener" style="color:${C.accent}">네이버</a>
        <a href="${kk}" target="_blank" rel="noopener" style="color:${C.accent}">카카오</a>
        <a href="${gg}" target="_blank" rel="noopener" style="color:${C.accent}">구글위성</a>
      </div></div>`;
  }, []);

  /** 상태값대로 마커·원을 다시 그린다. 저장이 실패하면 이걸로 제자리에 돌린다 */
  const refreshZone = useCallback(
    (id: number) => {
      const kakao = getKakao();
      const m = marksRef.current.get(id);
      const z = zonesRef.current.find((x) => x.id === id);
      if (!kakao || !m || !z || !iconRef.current) return;
      const kind = kindOf(z);
      const hue = kind === "edit" ? EDIT_HUE : kind === "far" ? FAR_HUE : NEAR_HUE;
      const pos = new kakao.maps.LatLng(z.lat, z.lot);
      m.marker.setPosition(pos);
      m.marker.setImage(iconRef.current[kind]);
      m.marker.setZIndex(kind === "edit" ? 10 : 1);
      m.circle.setPosition(pos);
      m.circle.setRadius(z.r);
      m.circle.setOptions(
        kind === "edit"
          ? { strokeColor: hue, fillColor: hue, strokeWeight: 3, strokeStyle: "shortdash", strokeOpacity: 1, fillOpacity: 0.22 }
          : { strokeColor: hue, fillColor: hue, strokeWeight: 1, strokeStyle: "solid", strokeOpacity: 0.9, fillOpacity: 0.18 }
      );
    },
    [kindOf]
  );

  // ── 저장 ──────────────────────────────────────────────────
  const applyRow = useCallback((row: ShopZoneRow) => {
    const z = toZone(row);
    setZones((prev) => prev.map((x) => (x.id === z.id ? z : x)));
  }, []);

  const setSavingFlag = (id: number, on: boolean) =>
    setSaving((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  /**
   * 한 구역을 DB 에 저장한다. 성공하면 서버가 돌려준 값으로 바꾼다.
   * quiet 이면 알림을 띄우지 않는다(여러 곳을 한꺼번에 저장할 때).
   */
  const save = useCallback(
    async (id: number, next: { lat: number; lot: number; r: number }, quiet = false) => {
      const z = zonesRef.current.find((x) => x.id === id);
      if (!z) return "fail" as const;
      const m = marksRef.current.get(id);
      // 저장이 끝나기 전에 또 끌면 수정일시가 어긋나 충돌이 난다. 끝날 때까지 잠근다
      m?.marker.setDraggable(false);
      setSavingFlag(id, true);
      try {
        const row = await patchShopZone({
          shop_zone_id: id,
          lat: fix8(next.lat),
          lot: fix8(next.lot),
          radius_m: next.r,
          mdfcn_dt: z.mdfcn,
        });
        if (row) applyRow(row);
        if (!quiet) {
          const o = origRef.current.get(id);
          const bits: string[] = [];
          const mv = row ? movedM(toZone(row), z) : 0;
          if (mv) bits.push(`${mv.toLocaleString()}m 이동`);
          if (row && row.radius_m !== z.r) bits.push(`반경 ${z.r} → ${row.radius_m}m`);
          if (!bits.length && o) bits.push("값 저장");
          setToast({ html: `<b>${esc(z.nm)}</b> 저장함 · ${bits.join(" · ")}` });
        }
        return "ok" as const;
      } catch (err) {
        const { code, message, current } = readShopZoneError(err);
        if (code === SHOP_ZONE_CONFLICT && current) {
          applyRow(current);
          if (!quiet)
            setToast({
              html: `<b>${esc(z.nm)}</b> — 다른 사용자가 먼저 수정했다. 지금 DB 값으로 바꿨다. 다시 확인하고 고친다.`,
              bad: true,
            });
          return "conflict" as const;
        }
        refreshZone(id);
        if (!quiet)
          setToast({ html: `<b>${esc(z.nm)}</b> 저장하지 못했다 — ${esc(message)}`, bad: true });
        return "fail" as const;
      } finally {
        setSavingFlag(id, false);
        m?.marker.setDraggable(true);
      }
    },
    [applyRow, refreshZone]
  );

  // 지도 이벤트는 처음 만든 함수를 계속 붙잡고 있으므로 최신 함수를 ref 로 넘긴다
  const saveRef = useRef(save);
  saveRef.current = save;

  // ── 선택 · 반경 패널 ─────────────────────────────────────────
  const select = useCallback(
    (id: number, move: boolean, openInfo: boolean) => {
      setCurrentId(id);
      // 반경 패널은 우클릭한 그 지점 것이다. 다른 곳을 고르면 닫는다
      setRTargetId((t) => (t !== null && t !== id ? null : t));
      const z = zonesRef.current.find((x) => x.id === id);
      const map = mapRef.current;
      const kakao = getKakao();
      if (!z || !map || !kakao) return;
      const pos = new kakao.maps.LatLng(z.lat, z.lot);
      if (move) {
        map.setLevel(ZOOM_PICK);
        map.panTo(pos);
      }
      if (openInfo) {
        infowRef.current.setContent(popupHtml(z));
        infowRef.current.setPosition(pos);
        infowRef.current.open(map);
      } else {
        infowRef.current?.close();
      }
    },
    [popupHtml]
  );
  const selectRef = useRef(select);
  selectRef.current = select;

  const openRadius = useCallback((id: number) => {
    const z = zonesRef.current.find((x) => x.id === id);
    if (!z) return;
    setRTargetId(id);
    setRInput(String(z.r));
    setRMsg("");
  }, []);
  const openRadiusRef = useRef(openRadius);
  openRadiusRef.current = openRadius;

  // 저장·되돌리기·충돌로 반경이 바뀌면 입력칸도 저장된 값으로 맞춘다
  const rSavedR =
    rTargetId !== null ? zones.find((z) => z.id === rTargetId)?.r : undefined;
  useEffect(() => {
    if (rSavedR !== undefined) setRInput(String(rSavedR));
  }, [rSavedR]);

  const rInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (rTargetId !== null) {
      rInputRef.current?.focus();
      rInputRef.current?.select();
    }
  }, [rTargetId]);

  const applyRadius = async (value?: number) => {
    if (rTargetId === null) return;
    const v = Math.round(Number(value ?? rInput));
    if (!Number.isFinite(v) || v < RADIUS_MIN || v > RADIUS_MAX) {
      setRMsg(`반경은 ${RADIUS_MIN} ~ ${RADIUS_MAX} m 사이로 넣는다.`);
      return;
    }
    const z = zonesRef.current.find((x) => x.id === rTargetId);
    if (!z) return;
    setRInput(String(v));
    if (v === z.r) return;
    await save(z.id, { lat: z.lat, lot: z.lot, r: v });
  };

  // ── 지도 만들기 ─────────────────────────────────────────────
  // 높이를 실측해 넣기 전에는 만들지 않는다. 0 높이에서 만들면 타일이 끝까지 안 올라온다
  const canCreateMap = isAllAdmin && (isMobile || bodyHeight > 0);
  useEffect(() => {
    if (!canCreateMap || mapRef.current || !mapElRef.current) return;
    const kakao = getKakao();
    if (!kakao?.maps) {
      setMapFailed(true);
      return;
    }
    let cancelled = false;
    // 키가 거부되면 load 콜백이 오지 않는다. 시간을 두고 확인한다
    const timer = window.setTimeout(() => {
      if (!mapRef.current) setMapFailed(true);
    }, 5000);
    kakao.maps.load(() => {
      if (cancelled || !mapElRef.current) return;
      try {
        const map = new kakao.maps.Map(mapElRef.current, {
          center: new kakao.maps.LatLng(36.3, 127.9),
          level: 12,
        });
        map.addControl(new kakao.maps.ZoomControl(), kakao.maps.ControlPosition.LEFT);
        infowRef.current = new kakao.maps.InfoWindow({ removable: true, zIndex: 20 });
        iconRef.current = {
          near: pinImage(kakao, "near"),
          far: pinImage(kakao, "far"),
          edit: pinImage(kakao, "edit"),
        };
        mapRef.current = map;
        setMapReady(true);
      } catch (e) {
        console.error(e);
        setMapFailed(true);
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canCreateMap]);

  // 지도 영역 크기가 바뀌면 다시 계산한다. 안 하면 타일이 갱신되지 않는다
  useEffect(() => {
    const el = mapElRef.current;
    const map = mapRef.current;
    if (!mapReady || !el || !map || typeof ResizeObserver === "undefined") return;
    let last = "";
    const ro = new ResizeObserver(() => {
      const key = el.clientWidth + "x" + el.clientHeight;
      if (key === last || el.clientWidth === 0 || el.clientHeight === 0) return;
      last = key;
      const c = map.getCenter();
      map.relayout();
      map.setCenter(c);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mapReady]);

  // ── 마커 동기화 ─────────────────────────────────────────────
  const fittedRef = useRef(false);
  useEffect(() => {
    const kakao = getKakao();
    const map = mapRef.current;
    if (!mapReady || !kakao || !map || !iconRef.current) return;
    const marks = marksRef.current;
    const alive = new Set(zones.map((z) => z.id));

    for (const [id, m] of Array.from(marks.entries())) {
      if (!alive.has(id)) {
        m.marker.setMap(null);
        m.circle.setMap(null);
        marks.delete(id);
      }
    }
    for (const z of zones) {
      if (!marks.has(z.id)) {
        const pos = new kakao.maps.LatLng(z.lat, z.lot);
        const circle = new kakao.maps.Circle({ center: pos, radius: z.r });
        circle.setMap(map);
        const marker = new kakao.maps.Marker({
          position: pos,
          draggable: true,
          title: z.nm + " — 끌어서 이동, 우클릭으로 반경 변경",
        });
        marker.setMap(map);
        const id = z.id;
        kakao.maps.event.addListener(marker, "click", () => selectRef.current(id, false, true));
        kakao.maps.event.addListener(marker, "rightclick", () => {
          selectRef.current(id, false, false);
          openRadiusRef.current(id);
        });
        // 끄는 동안 원이 따라오게 한다. 어디까지 덮는지 보면서 놓을 수 있다
        kakao.maps.event.addListener(marker, "dragstart", () => infowRef.current?.close());
        kakao.maps.event.addListener(marker, "drag", () => circle.setPosition(marker.getPosition()));
        kakao.maps.event.addListener(marker, "dragend", () => {
          const p = marker.getPosition();
          const cur = zonesRef.current.find((x) => x.id === id);
          if (!cur) return;
          setCurrentId(id);
          saveRef.current(id, { lat: p.getLat(), lot: p.getLng(), r: cur.r });
        });
        marks.set(z.id, { marker, circle });
      }
      refreshZone(z.id);
    }

    if (!fittedRef.current && zones.length) {
      fittedRef.current = true;
      // 지도를 만든 뒤 컨테이너 크기가 바뀌었을 수 있다. 실제 크기로 맞춘 다음 범위를 잡는다
      map.relayout();
      const b = new kakao.maps.LatLngBounds();
      for (const z of zones) b.extend(new kakao.maps.LatLng(z.lat, z.lot));
      map.setBounds(b);
    }
  }, [zones, mapReady, refreshZone]);

  // 지도 유형 · 지적편집도
  useEffect(() => {
    const kakao = getKakao();
    if (!mapReady || !kakao) return;
    mapRef.current.setMapTypeId(kakao.maps.MapTypeId[mapType]);
  }, [mapType, mapReady]);
  useEffect(() => {
    const kakao = getKakao();
    if (!mapReady || !kakao) return;
    const id = kakao.maps.MapTypeId.USE_DISTRICT;
    if (district) mapRef.current.addOverlayMapTypeId(id);
    else mapRef.current.removeOverlayMapTypeId(id);
  }, [district, mapReady]);

  // ── 목록 ──────────────────────────────────────────────────
  const shown = useMemo(() => {
    const key = q.trim().toLowerCase();
    return zones.filter((z) => {
      if (farOnly && !isFar(z)) return false;
      if (!key) return true;
      return `${z.nm} ${z.addr} ${z.ogdp} ${z.sido} ${z.sgg}`.toLowerCase().includes(key);
    });
  }, [zones, q, farOnly]);

  const groups = useMemo(() => {
    const out: { name: string; items: Zone[] }[] = [];
    for (const z of shown) {
      const name = `${z.sido} ${z.sgg}`.trim();
      const last = out[out.length - 1];
      if (last && last.name === name) last.items.push(z);
      else out.push({ name, items: [z] });
    }
    return out;
  }, [shown]);

  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (currentId === null) return;
    const el = listRef.current?.querySelector(`[data-id="${currentId}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: "nearest" });
  }, [currentId]);

  const fitAll = () => {
    const kakao = getKakao();
    const map = mapRef.current;
    if (!map || !kakao) return;
    const list = shown.length ? shown : zones;
    if (!list.length) return;
    map.relayout();
    if (list.length === 1) {
      map.setLevel(ZOOM_PICK);
      map.panTo(new kakao.maps.LatLng(list[0].lat, list[0].lot));
      return;
    }
    const b = new kakao.maps.LatLngBounds();
    for (const z of list) b.extend(new kakao.maps.LatLng(z.lat, z.lot));
    map.setBounds(b);
  };

  // ── 여러 곳 한꺼번에 저장 (엑셀 불러오기 · 전부 되돌리기) ───────────
  const saveMany = async (
    title: string,
    items: { id: number; lat: number; lot: number; r: number }[]
  ) => {
    let ok = 0;
    let conflict = 0;
    let fail = 0;
    for (let i = 0; i < items.length; i++) {
      setBulk(`${title} 저장 중 ${i + 1}/${items.length}`);
      const it = items[i];
      const res = await saveRef.current(it.id, it, true);
      if (res === "ok") ok++;
      else if (res === "conflict") conflict++;
      else fail++;
    }
    setBulk(null);
    const bits = [`${title} · 저장 <b>${ok}</b>곳`];
    if (conflict) bits.push(`다른 사용자가 먼저 고쳐 건너뜀 ${conflict}곳`);
    if (fail) bits.push(`실패 ${fail}곳`);
    setToast({ html: bits.join(" · "), bad: ok === 0 || fail > 0 });
  };

  const undoAll = async () => {
    const items = changedIds
      .map((id) => origRef.current.get(id))
      .filter((o): o is Zone => !!o)
      .map((o) => ({ id: o.id, lat: o.lat, lot: o.lot, r: o.r }));
    if (!items.length) return;
    if (!window.confirm(`이번 접속에서 고친 ${items.length}곳을 처음 값으로 되돌려 DB 에 저장합니다.`)) return;
    setRTargetId(null);
    await saveMany("전부 되돌리기", items);
  };

  const undoOne = async (id: number) => {
    const o = origRef.current.get(id);
    if (!o) return;
    await save(id, { lat: o.lat, lot: o.lot, r: o.r });
  };

  // ── 엑셀 ──────────────────────────────────────────────────
  const exportXlsx = () => {
    const wb = XLSX.utils.book_new();
    const all = zones.map((z) => ({
      shop_zone_id: z.id,
      ogdp_id: z.ogdp,
      사업소명: z.nm,
      시도: z.sido,
      시군구: z.sgg,
      주소: z.addr,
      위도: fix8(z.lat),
      경도: fix8(z.lot),
      반경_m: z.r,
      이번수정: changedIds.includes(z.id) ? "Y" : "",
      수정일시: z.mdfcn,
    }));
    const ws = XLSX.utils.json_to_sheet(all);
    ws["!cols"] = [{ wch: 12 }, { wch: 10 }, { wch: 28 }, { wch: 11 }, { wch: 10 }, { wch: 42 },
      { wch: 13 }, { wch: 13 }, { wch: 9 }, { wch: 8 }, { wch: 19 }];
    XLSX.utils.book_append_sheet(wb, ws, "전체");

    const changed = zones.filter((z) => changedIds.includes(z.id));
    if (changed.length) {
      const rows = changed.map((z) => {
        const o = origRef.current.get(z.id)!;
        return {
          shop_zone_id: z.id, ogdp_id: z.ogdp, 사업소명: z.nm, 시도: z.sido, 시군구: z.sgg, 주소: z.addr,
          원래위도: fix8(o.lat), 원래경도: fix8(o.lot), 원래반경: o.r,
          수정위도: fix8(z.lat), 수정경도: fix8(z.lot), 수정반경: z.r,
          이동거리_m: movedM(z, o),
          좌표변경: movedM(z, o) > 0 ? "Y" : "",
          반경변경: z.r !== o.r ? "Y" : "",
        };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "이번 수정");
    }
    XLSX.writeFile(wb, `사업소좌표_${stamp()}.xlsx`);
  };

  const fileInRef = useRef<HTMLInputElement | null>(null);
  const importFile = async (file: File) => {
    let wb: XLSX.WorkBook;
    try {
      wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
    } catch {
      setToast({ html: "<b>파일을 읽지 못했다.</b> 엑셀(.xlsx) 이나 CSV 인지 확인한다.", bad: true });
      return;
    }
    // 값이 있는 시트를 고른다. 수정분 → 이번 수정 → 전체 → 나머지 순
    const order = Array.from(new Set(["수정분", "이번 수정", "전체", ...wb.SheetNames])).filter(
      (n) => wb.Sheets[n]
    );
    let plan: ImportPlan | null = null;
    for (const name of order) {
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[name], { defval: null });
      if (!rows.length) continue;
      const cols = pickCols(Object.keys(rows[0]));
      if ((cols.zid || cols.ogdp) && (cols.lat || cols.r)) {
        plan = planImport(name, rows, cols, zonesRef.current);
        break;
      }
    }
    if (!plan) {
      setToast({
        html:
          "<b>읽을 열을 찾지 못했다.</b><br>shop_zone_id 또는 ogdp_id 와, 수정위도·수정경도 또는 수정반경 열이 있어야 한다.",
        bad: true,
      });
      return;
    }
    const notes: string[] = [];
    if (plan.nochange) notes.push(`지금 값과 같아 넘김 ${plan.nochange}건`);
    if (plan.bad) notes.push(`값이 범위를 벗어나 건너뜀 ${plan.bad}건`);
    if (plan.missing.length) notes.push(`지도에 없는 구역 ${plan.missing.length}건 (${plan.missing.slice(0, 5).join(", ")})`);
    if (plan.ambiguous.length) notes.push(`사업소에 구역이 여럿이라 못 맞춘 것 ${plan.ambiguous.length}건`);
    if (!plan.patches.length) {
      setToast({ html: `<b>${esc(file.name)}</b> · 시트 '${esc(plan.sheet)}' · 바꿀 곳이 없다.<br>${notes.join(" · ")}`, bad: true });
      return;
    }
    const lines = plan.patches
      .slice(0, 15)
      .map((p) => `- ${p.zone.nm}: ${p.zone.r}m → ${p.r}m, ${movedM({ ...p.zone, lat: p.lat, lot: p.lot }, p.zone)}m 이동`);
    if (plan.patches.length > 15) lines.push(`… 외 ${plan.patches.length - 15}곳`);
    const ok = window.confirm(
      `${file.name} (시트 '${plan.sheet}')\n\n${plan.patches.length}곳을 DB 에 바로 저장합니다.\n\n${lines.join("\n")}` +
        (notes.length ? `\n\n${notes.join("\n")}` : "")
    );
    if (!ok) return;
    await saveMany(
      `${file.name}`,
      plan.patches.map((p) => ({ id: p.zone.id, lat: p.lat, lot: p.lot, r: p.r }))
    );
  };

  // ── 렌더 ──────────────────────────────────────────────────
  if (!isAllAdmin) {
    return (
      <PageLayout withBreadcrumb withoutFooter>
        <Denied>인플랩 관리자만 볼 수 있는 화면입니다.</Denied>
      </PageLayout>
    );
  }

  const rZone = rTargetId !== null ? zones.find((z) => z.id === rTargetId) ?? null : null;
  const rOrig = rZone ? orig(rZone.id) : undefined;
  const loadError = error ? readShopZoneError(error).message : null;

  return (
    <PageLayout withBreadcrumb withoutBody>
      <Body ref={bodyRef} style={isMobile || bodyHeight === 0 ? undefined : { height: bodyHeight }}>
        <Bar>
          <h1>임대사업소 안전구역</h1>
          <span className="sub">
            <strong>{shown.length}</strong>곳
          </span>
          <span className="spacer" />
          {(saving.size > 0 || bulk) && <span className="saving">{bulk ?? "저장 중…"}</span>}
          {changedIds.length > 0 && <span className="badge">이번 접속 수정 {changedIds.length}곳</span>}
          <button type="button" onClick={() => refetch()} disabled={isFetching || !!bulk}>
            {isFetching ? "불러오는 중…" : "새로고침"}
          </button>
          <button type="button" onClick={() => fileInRef.current?.click()} disabled={!zones.length || !!bulk}>
            엑셀 불러오기
          </button>
          <input
            ref={fileInRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              // 같은 파일을 다시 고를 수 있게 비워 둔다
              e.target.value = "";
              if (f) importFile(f);
            }}
          />
          <button type="button" onClick={exportXlsx} disabled={!zones.length}>
            엑셀 내보내기
          </button>
          <button type="button" onClick={undoAll} disabled={!changedIds.length || !!bulk}>
            전부 되돌리기
          </button>
          <span className="seg" role="group" aria-label="지도 유형">
            {MAP_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-pressed={mapType === t.key}
                onClick={() => setMapType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </span>
          <button type="button" aria-pressed={district} title="부지 경계 확인용" onClick={() => setDistrict((v) => !v)}>
            지적편집도
          </button>
          <button
            type="button"
            aria-pressed={farOnly}
            onClick={() => {
              setFarOnly((v) => !v);
            }}
          >
            추출좌표 1km+ 만
          </button>
          <button type="button" onClick={fitAll}>
            전체보기
          </button>
        </Bar>

        <Main>
          <MapArea
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const f = e.dataTransfer?.files?.[0];
              if (!f) return;
              e.preventDefault();
              importFile(f);
            }}
          >
            <div ref={mapElRef} className="map" />

            {mapFailed && (
              <div className="setup">
                <div>
                  <h2>지도를 불러오지 못했다</h2>
                  <p>
                    카카오맵 JS 키는 <strong>등록된 도메인에서만</strong> 동작한다. 이 주소(
                    <code>{window.location.origin}</code>)가 카카오 개발자 콘솔 → 앱 → 플랫폼 → Web
                    에 있는지 확인한다.
                  </p>
                  <p>오른쪽 목록과 검색은 지도 없이도 쓸 수 있다.</p>
                </div>
              </div>
            )}

            {toast && (
              <div className={`toast${toast.bad ? " bad" : ""}`}>
                <button type="button" className="tx" aria-label="닫기" onClick={() => setToast(null)}>
                  &times;
                </button>
                <div dangerouslySetInnerHTML={{ __html: toast.html }} />
              </div>
            )}

            {rZone && (
              <div className="radius">
                <div className="rn">{rZone.nm}</div>
                <div className="rs">
                  {rZone.lat.toFixed(8)}, {rZone.lot.toFixed(8)} · ogdp {rZone.ogdp}
                  {rOrig && isChanged(rZone, rOrig) && (
                    <>
                      <br />
                      처음 값에서 {movedM(rZone, rOrig).toLocaleString()}m 이동 · 반경 {rOrig.r} → {rZone.r}m
                    </>
                  )}
                  {rMsg && (
                    <>
                      <br />
                      <span className="warn">{rMsg}</span>
                    </>
                  )}
                </div>
                <div className="row">
                  <input
                    ref={rInputRef}
                    type="number"
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    step={10}
                    value={rInput}
                    onChange={(e) => setRInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applyRadius();
                      }
                      if (e.key === "Escape") setRTargetId(null);
                    }}
                  />
                  <span className="rs">m</span>
                  {RADIUS_PRESETS.map((r) => (
                    <button key={r} type="button" className="preset" disabled={saving.has(rZone.id)} onClick={() => applyRadius(r)}>
                      {r}
                    </button>
                  ))}
                </div>
                <div className="row">
                  <button type="button" className="done" disabled={saving.has(rZone.id)} onClick={() => applyRadius()}>
                    {saving.has(rZone.id) ? "저장 중…" : "적용"}
                  </button>
                  <button
                    type="button"
                    className="reset"
                    disabled={!rOrig || !isChanged(rZone, rOrig) || saving.has(rZone.id)}
                    onClick={() => undoOne(rZone.id)}
                  >
                    이 지점 되돌리기
                  </button>
                  <button type="button" className="close" onClick={() => setRTargetId(null)}>
                    닫기
                  </button>
                </div>
              </div>
            )}
          </MapArea>

          <Aside>
            <div className="search">
              <input
                type="search"
                placeholder="사업소명 · 주소 · ogdp_id 검색"
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="list" ref={listRef}>
              {isLoading && <p className="empty">불러오는 중…</p>}
              {loadError && <p className="empty warn">불러오지 못했다 — {loadError}</p>}
              {!isLoading && !loadError && !shown.length && <p className="empty">해당하는 사업소가 없다.</p>}
              {groups.map((g) => (
                <div key={g.name}>
                  <div className="grp">
                    <span>{g.name || "(지역 없음)"}</span>
                    <span>{g.items.length}</span>
                  </div>
                  {g.items.map((z) => {
                    const o = orig(z.id);
                    const edited = isChanged(z, o);
                    let tail: JSX.Element | null = null;
                    if (edited && o) {
                      const mv = movedM(z, o);
                      const bits: string[] = [];
                      if (mv) bits.push(`${mv.toLocaleString()}m 이동`);
                      if (z.r !== o.r) bits.push(`반경 ${o.r} → ${z.r}m`);
                      tail = <span className="chg"> · {bits.join(" · ")}</span>;
                    } else if (z.diff !== null) {
                      tail = <span className={isFar(z) ? "far" : ""}> · 추출 {z.diff.toLocaleString()}m</span>;
                    }
                    return (
                      <button
                        key={z.id}
                        type="button"
                        data-id={z.id}
                        className={`item${edited ? " edited" : ""}`}
                        aria-current={z.id === currentId ? "true" : undefined}
                        onClick={() => select(z.id, true, false)}
                      >
                        <div className="nm">{z.nm}</div>
                        <div className="meta">
                          {z.lat.toFixed(5)}, {z.lot.toFixed(5)} · 반경 {z.r}m{tail}
                          {saving.has(z.id) && <span className="chg"> · 저장 중</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="legend">
              <span className="dot" style={{ background: NEAR_HUE }} />운행기록 좌표와 1km 이내
              <br />
              <span className="dot" style={{ background: FAR_HUE }} />1km 넘게 차이 — 차고지 재확인 권장
              <br />
              <span className="dot" style={{ background: EDIT_HUE }} />이번 접속에서 고침 (점선 원)
              <br />
              원은 실제 억제 반경이다. 옮기거나 반경을 바꾸면 바로 DB 에 저장된다.
            </div>
          </Aside>
        </Main>
      </Body>
    </PageLayout>
  );
};

export default ShopZone;

// ── 스타일 ───────────────────────────────────────────────────
// 바탕·선은 예전 단독 HTML 배색을 쓴다. 선택·포커스·눌린 버튼은 다른 메뉴와 같은 농콜 초록이다.
// 수정 표시(자홍)는 지도 위에서 눈에 띄어야 해서 그대로 둔다
const C = {
  bg: "#f4f5f2",
  panel: "#ffffff",
  line: "#d9dcd4",
  fg: "#22261f",
  muted: "#6c7266",
  accent: theme.statusColor.MAIN,
  accentSk: "#e6f4ea",
  warn: "#9c2f2f",
  edit: "#cc1472",
  editSk: "#fde3f0",
  mono: 'Consolas, "IBM Plex Mono", ui-monospace, monospace',
};

const Denied = styled.div`
  padding: 80px 20px;
  text-align: center;
  color: ${C.muted};
  font-size: 15px;
`;

// 다른 화면의 본문 상자(PageLayout Body)와 같은 둥근 모서리·그림자. 좌우 여백은 PageLayout 이 준다
const Body = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 0;
  background: ${C.bg};
  color: ${C.fg};
  font-size: 14px;
  border-radius: 20px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  margin-bottom: ${BODY_MARGIN_BOTTOM}px;
  @media (max-width: ${md}px) {
    height: auto;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 0;
  }
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 18px;
  background: ${C.panel};
  border-bottom: 1px solid ${C.line};

  h1 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
  .sub {
    color: ${C.muted};
    font-size: 12px;
  }
  .spacer {
    flex: 1 1 auto;
  }
  .saving {
    font-size: 12px;
    color: ${C.accent};
  }
  .badge {
    font-family: ${C.mono};
    font-size: 11px;
    font-weight: 500;
    color: #fff;
    background: ${C.edit};
    border-radius: 10px;
    padding: 2px 8px;
  }
  button {
    font: inherit;
    font-size: 12px;
    color: ${C.fg};
    background: transparent;
    border: 1px solid ${C.line};
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
  }
  button:hover:not(:disabled) {
    border-color: ${C.accent};
    color: ${C.accent};
  }
  button[aria-pressed="true"] {
    background: ${C.accent};
    border-color: ${C.accent};
    color: #fff;
  }
  button:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .seg {
    display: inline-flex;
  }
  .seg button {
    border-radius: 0;
    margin-left: -1px;
  }
  .seg button:first-of-type {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }
  .seg button:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  .seg button[aria-pressed="true"] {
    position: relative;
    z-index: 1;
  }
`;

const Main = styled.div`
  display: grid;
  grid-template-columns: 8fr minmax(240px, 2fr);
  min-height: 0;
  @media (max-width: ${md}px) {
    grid-template-columns: 1fr;
    grid-template-rows: 60vh auto;
  }
`;

const MapArea = styled.div`
  position: relative;
  min-height: 0;
  background: ${C.bg};

  .map {
    position: absolute;
    inset: 0;
  }

  .setup {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: grid;
    place-content: center;
    padding: 24px;
    background: ${C.bg};
    line-height: 1.7;
    font-size: 13px;
  }
  .setup > div {
    max-width: 560px;
  }
  .setup h2 {
    margin: 0 0 10px;
    font-size: 15px;
    color: ${C.warn};
  }
  .setup code {
    font-family: ${C.mono};
    font-size: 12px;
    background: ${C.panel};
    border: 1px solid ${C.line};
    border-radius: 3px;
    padding: 1px 5px;
  }

  .toast {
    position: absolute;
    z-index: 11;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: min(460px, calc(100% - 32px));
    background: ${C.panel};
    border: 1px solid ${C.line};
    border-left: 3px solid ${C.edit};
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    padding: 11px 34px 11px 13px;
    font-size: 12.5px;
    line-height: 1.6;
  }
  .toast.bad {
    border-left-color: ${C.warn};
  }
  .toast .tx {
    position: absolute;
    top: 6px;
    right: 8px;
    font-size: 17px;
    line-height: 1;
    cursor: pointer;
    color: ${C.muted};
    background: transparent;
    border: 0;
    padding: 2px 5px;
  }

  .radius {
    position: absolute;
    z-index: 9;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    width: min(400px, calc(100% - 32px));
    background: ${C.panel};
    border: 1px solid ${C.line};
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    padding: 12px 14px;
  }
  .radius .rn {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 3px;
  }
  .radius .rs {
    font-size: 11px;
    color: ${C.muted};
    font-family: ${C.mono};
    line-height: 1.5;
  }
  .radius .warn {
    color: ${C.warn};
  }
  .radius .row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .radius input[type="number"] {
    width: 86px;
    font-family: ${C.mono};
    font-size: 13px;
    color: ${C.fg};
    background: ${C.bg};
    border: 1px solid ${C.line};
    border-radius: 4px;
    padding: 5px 8px;
  }
  .radius input:focus {
    outline: none;
    border-color: ${C.accent};
  }
  .radius button {
    font-size: 12px;
    cursor: pointer;
    color: ${C.fg};
    background: transparent;
    border: 1px solid ${C.line};
    border-radius: 4px;
    padding: 4px 9px;
  }
  .radius button:hover:not(:disabled) {
    border-color: ${C.accent};
    color: ${C.accent};
  }
  .radius button:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .radius .preset {
    font-family: ${C.mono};
  }
  .radius .done {
    border-color: ${C.edit};
    color: ${C.edit};
  }
  .radius .reset {
    border-color: ${C.warn};
    color: ${C.warn};
  }
  .radius .close {
    margin-left: auto;
  }
`;

const Aside = styled.aside`
  border-left: 1px solid ${C.line};
  background: ${C.panel};
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;
  min-width: 0;
  @media (max-width: ${md}px) {
    border-left: 0;
    border-top: 1px solid ${C.line};
  }

  .search {
    padding: 10px;
    border-bottom: 1px solid ${C.line};
  }
  .search input {
    width: 100%;
    box-sizing: border-box;
    font-size: 13px;
    color: ${C.fg};
    background: ${C.bg};
    border: 1px solid ${C.line};
    border-radius: 4px;
    padding: 6px 9px;
  }
  .search input:focus {
    outline: none;
    border-color: ${C.accent};
  }
  .list {
    overflow-y: auto;
    min-height: 0;
  }
  .grp {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${C.bg};
    border-bottom: 1px solid ${C.line};
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    color: ${C.muted};
    display: flex;
    justify-content: space-between;
  }
  .item {
    display: block;
    width: 100%;
    text-align: left;
    font: inherit;
    background: transparent;
    border: 0;
    border-bottom: 1px solid ${C.line};
    border-left: 3px solid transparent;
    padding: 8px 10px;
    cursor: pointer;
    min-width: 0;
    color: ${C.fg};
  }
  .item:hover {
    background: ${C.accentSk};
  }
  .item[aria-current="true"] {
    background: ${C.accentSk};
    border-left-color: ${C.accent};
  }
  .item.edited {
    border-left-color: ${C.edit};
  }
  .item.edited[aria-current="true"] {
    background: ${C.editSk};
  }
  .item .nm {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }
  .item .meta {
    margin-top: 3px;
    font-family: ${C.mono};
    font-size: 10.5px;
    color: ${C.muted};
    overflow-wrap: anywhere;
  }
  .item .far {
    color: ${C.warn};
    font-weight: 500;
  }
  .item .chg {
    color: ${C.edit};
    font-weight: 500;
  }
  .legend {
    border-top: 1px solid ${C.line};
    padding: 9px 10px;
    font-size: 11px;
    color: ${C.muted};
    line-height: 1.6;
  }
  .legend .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    margin-right: 5px;
    vertical-align: -1px;
  }
  .empty {
    padding: 20px 12px;
    color: ${C.muted};
    font-size: 12px;
    margin: 0;
  }
  .empty.warn {
    color: ${C.warn};
  }
`;
